import React, { useEffect } from 'react';
import { Autocomplete, Skeleton, TextField } from '@mui/material';
import { useApiGet } from '../utils/httpClient';
import { getSpds } from '../endpoints/annualReports';

interface SPDObject {
  id: string;
  name: string;
  [key: string]: any; // Allow additional properties
}

// Base props interface
// keyId: instance identifier for React key and cache (e.g. page name, entity id).
// reportId: optional PEO report definition id; when set, SPD list is filtered by report access (only set on PEO Report detail).
interface BaseSPDSelectorProps {
  spd: string;
  keyId: string;
  reportId?: string | null;
  all?: boolean;
}

// When objectReturned is true, handler receives an object
interface SPDSelectorPropsWithObject extends BaseSPDSelectorProps {
  objectReturned: true;
  handleSpdSelect: (value: SPDObject | null) => void;
}

// When objectReturned is false (default), handler receives a string
interface SPDSelectorPropsWithString extends BaseSPDSelectorProps {
  objectReturned?: false;
  handleSpdSelect: (value: string | null) => void;
}

type SPDSelectorProps = SPDSelectorPropsWithObject | SPDSelectorPropsWithString;

const SPDSelector: React.FC<SPDSelectorProps> = ({
  spd,
  handleSpdSelect,
  keyId,
  reportId,
  all = false,
  objectReturned = false,
}) => {
  const { data } = useApiGet(
    ['spds', all, keyId, reportId ?? ''],
    getSpds,
    {
      enabled: true,
      retry: 1,
    },
    { all, reportId: reportId || undefined }
  );

  const spdList = data as SPDObject[] | undefined;

  useEffect(() => {
    if (spdList && spdList.length === 1 && !spd) {
      (handleSpdSelect as (value: string) => void)(spdList[0].id);
    }
  }, [spdList, spd, handleSpdSelect]);

  const onSpdSelect = (spdId: string | null) => {
    if (objectReturned && spdId) {
      const spdObject = spdList?.find((spdRecord) => spdRecord.id === spdId);
      (handleSpdSelect as (value: SPDObject | null) => void)(spdObject || null);
    } else {
      (handleSpdSelect as (value: string | null) => void)(spdId);
    }
  };

  return spdList ? (
    <Autocomplete
      key={keyId}
      options={spdList}
      size="small"
      value={spdList.find((spdRecord) => spdRecord.id === spd) || null}
      getOptionLabel={(option) => option.name || ''}
      onChange={(event, newValue) => onSpdSelect(newValue ? newValue.id : null)}
      renderInput={(params) => <TextField {...params} label="S/P/D" />}
    />
  ) : (
    <Skeleton>
      <TextField label="S/P/D" />
    </Skeleton>
  );
};

export default SPDSelector;
